import dayjs, { Dayjs } from "dayjs";
import { IsoDateTimeString } from "src/types/dateTime.ts";

export type SalesLeadActivityDueDateFilter =
	"all" | "overdue" | "today" | "tomorrow" | "thisWeek" | "nextWeek" | "custom";

export const salesLeadActivityDueDateFilters: SalesLeadActivityDueDateFilter[] = [
	"all",
	"overdue",
	"today",
	"tomorrow",
	"thisWeek",
	"nextWeek",
	"custom",
];

export interface SalesLeadActivityDueDateFilterState {
	dueDateFilter: SalesLeadActivityDueDateFilter;
	customStartDate: Dayjs | null;
	customEndDate: Dayjs | null;
}

export interface SalesLeadActivityDueDateFilterApiParams {
	dueFrom?: IsoDateTimeString | null;
	dueUntilExclusive?: IsoDateTimeString | null;
	onlyOverdue?: boolean;
	showOpen?: boolean;
	showCompleted?: boolean;
}

export function getSalesLeadActivityDueDateFilterApiParams({
	dueDateFilter,
	customStartDate,
	customEndDate,
	currentDate = dayjs(),
}: SalesLeadActivityDueDateFilterState & {
	currentDate?: Dayjs;
}): SalesLeadActivityDueDateFilterApiParams {
	const today = currentDate.startOf("day");

	switch (dueDateFilter) {
		case "all":
			return {};
		case "overdue":
			return {
				onlyOverdue: true,
				showOpen: true,
				showCompleted: false,
			};
		case "today":
			return dateRange(today, today.add(1, "day"));
		case "tomorrow": {
			const tomorrow = today.add(1, "day");
			return dateRange(tomorrow, tomorrow.add(1, "day"));
		}
		case "thisWeek": {
			const startOfWeek = getStartOfIsoWeek(today);
			return dateRange(startOfWeek, startOfWeek.add(1, "week"));
		}
		case "nextWeek": {
			const startOfNextWeek = getStartOfIsoWeek(today).add(1, "week");
			return dateRange(startOfNextWeek, startOfNextWeek.add(1, "week"));
		}
		case "custom":
			return {
				...(customStartDate != null && { dueFrom: toStartOfDayInstant(customStartDate) }),
				...(customEndDate != null && { dueUntilExclusive: toStartOfDayInstant(customEndDate.add(1, "day")) }),
			};
	}
}

/**
 * Due ranges cover whole days in the browser's time zone.
 */
function dateRange(startDate: Dayjs, endDateExclusive: Dayjs): SalesLeadActivityDueDateFilterApiParams {
	return {
		dueFrom: toStartOfDayInstant(startDate),
		dueUntilExclusive: toStartOfDayInstant(endDateExclusive),
	};
}

function toStartOfDayInstant(date: Dayjs): IsoDateTimeString {
	return date.startOf("day").toISOString();
}

function getStartOfIsoWeek(date: Dayjs): Dayjs {
	return date.subtract((date.day() + 6) % 7, "day").startOf("day");
}
